import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Images, Loader2, Plus, Search } from 'lucide-react';
import { quoteService, type PhotoQuoteStatus, type PhotoQuoteSummary } from '@/services/quoteService';
import PageHeader from '@/components/shared/PageHeader';
import { usePageTitle } from '@/hooks/usePageTitle';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 50;

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All' },
  { value: 'draft', label: 'Drafts' },
  { value: 'sent,viewed', label: 'With recipient' },
  { value: 'ordered', label: 'Ordered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const STATUS_STYLES: Record<PhotoQuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  viewed: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  ordered: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  cancelled: 'bg-destructive/15 text-destructive',
};

const STATUS_LABELS: Record<PhotoQuoteStatus, string> = {
  draft: 'Draft',
  sent: 'Sent',
  viewed: 'Opened',
  ordered: 'Ordered',
  cancelled: 'Cancelled',
};

function formatCurrency(value: string | number | null | undefined): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n == null || Number.isNaN(n)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(value),
  );
}

/**
 * Photo Quotes list. A quote is built here and read in the Trade Portal by its recipient:
 * a customer, or a sales agent (optionally for a customer).
 */
export default function QuotesTable() {
  usePageTitle('Photo Quotes');
  const navigate = useNavigate();

  const [quotes, setQuotes] = useState<PhotoQuoteSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await quoteService.list({
        status: status || undefined,
        search: search.trim() || undefined,
        limit: PAGE_SIZE,
        offset,
      });
      setQuotes(result.data);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, [status, search, offset]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <PageHeader
          title="Photo Quotes"
          subtitle={total === 1 ? 'quote' : 'quotes'}
          count={total}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Photo Quotes' }]}
        />
        <Button onPress={() => navigate('/quotes/new')}>
          <Plus className="size-4" />
          New Photo Quote
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1 max-w-sm">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setOffset(0);
              setSearch(e.target.value);
            }}
            placeholder="Search quote number, title, customer or agent"
            className="pl-8"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {STATUS_FILTERS.map((filter) => (
            <Button
              key={filter.label}
              intent={status === filter.value ? 'primary' : 'outline'}
              size="sm"
              onPress={() => {
                setOffset(0);
                setStatus(filter.value);
              }}
            >
              {filter.label}
            </Button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Quote</TableHead>
              <TableHead>Recipient</TableHead>
              <TableHead className="text-right">Products</TableHead>
              <TableHead className="text-right">Value</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Sent</TableHead>
              <TableHead>Prepared by</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && quotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                  <Loader2 className="mx-auto size-5 animate-spin" />
                </TableCell>
              </TableRow>
            )}
            {!loading && quotes.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="h-32 text-center">
                  <Images className="mx-auto mb-2 size-6 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    No photo quotes yet. Build one to put a selection of products in front of a customer.
                  </p>
                </TableCell>
              </TableRow>
            )}
            {quotes.map((quote) => (
              <TableRow
                key={quote.id}
                className="cursor-pointer"
                onClick={() => navigate(`/quotes/${quote.id}`)}
              >
                <TableCell>
                  <div className="font-medium">{quote.quote_number}</div>
                  {quote.title && (
                    <div className="text-xs text-muted-foreground">{quote.title}</div>
                  )}
                </TableCell>
                <TableCell>
                  {quote.recipient_type === 'agent' ? (
                    <>
                      <div>Agent: {quote.recipient_agent_name ?? quote.recipient_agent_id ?? '—'}</div>
                      {quote.company_name && (
                        <div className="text-xs text-muted-foreground">for {quote.company_name}</div>
                      )}
                    </>
                  ) : (
                    <>
                      <div>{quote.company_name ?? '—'}</div>
                      {quote.contact_name && (
                        <div className="text-xs text-muted-foreground">{quote.contact_name}</div>
                      )}
                    </>
                  )}
                </TableCell>
                <TableCell className="text-right tabular-nums">{quote.line_count}</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(quote.total_value)}</TableCell>
                <TableCell>
                  <Badge variant="secondary" className={cn('border-0', STATUS_STYLES[quote.status])}>
                    {STATUS_LABELS[quote.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(quote.sent_at)}</TableCell>
                <TableCell className="text-muted-foreground">{quote.created_by_name ?? '—'}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex gap-2">
            <Button
              intent="outline"
              size="sm"
              isDisabled={offset === 0}
              onPress={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              intent="outline"
              size="sm"
              isDisabled={offset + PAGE_SIZE >= total}
              onPress={() => setOffset(offset + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
